// Shared Lumi assistant wiring (ADR 0027). The headless LumiAssistantPanel
// (@lumi/ui) needs one app adapter per UI; this factory is that adapter's shared
// body, so each AppChrome contributes only its app-specific getters (its api
// instance, router, identity) instead of copy-pasting the request/navigate/
// context plumbing. Lives in app-kit (ADR 0026) because it imports axios +
// vue-router — the wider peer surface @lumi/ui is not allowed.
import type { AxiosInstance } from 'axios';
import type { Router } from 'vue-router';
import type { AssistantTransport, ModuleSwitchTarget, LumiModuleKey } from '@lumi/ui';
import { withDeepLink } from './moduleSwitcher';
import { useDialog } from '../composables/useDialog';
import { dispatchLumiChanged } from './dataChanged';

export interface AssistantTransportOptions {
  /** The app's tenant-scoped axios instance for `/api/assistant`. */
  api: AxiosInstance;
  router: Router;
  /** This app's module key — cross-module navigate targets differ from it. */
  module: LumiModuleKey;
  /** Current in-module route (e.g. `() => route.fullPath`). */
  route: () => string;
  /** The module-switcher targets, for cross-module navigation. */
  moduleTargets: () => ModuleSwitchTarget[];
  /** Current identity for page context (tenant is also sent via X-Tenant-Slug). */
  identity: () => { tenantSlug?: string; userId?: string; email?: string; locale?: string };
  /**
   * Accepted for compatibility, and inert in a converted app: since issue 195 (A10)
   * no app in the suite has an error-surfacing axios interceptor left to opt out of.
   * A write chooses where its failure is reported at its own call site, through
   * `useApiMutation`'s `errorChannel`.
   */
  suppressGlobalError?: boolean;
  /** The verb on the confirm button of the panel's destructive "new conversation" ask. */
  confirmLabel?: () => string;
}

// The chat turn drives the server agent loop (up to a few LLM iterations + tool
// calls); the app's default axios timeout (~15s) aborts a normal turn mid-flight
// and shows a false "send failed", even though the 5s poll would surface the
// reply. Give /chat a budget matching the server loop (M2).
const CHAT_TIMEOUT_MS = 180_000;

// Known entity-link routes per module (the tool manifests' entityLinkTypes).
// A ref whose (module,type) isn't mapped renders as plain text. Add a module's
// row here when it starts emitting `[[…]]` tokens.
const LINK_ROUTES: Record<string, Record<string, (id: string) => string>> = {
  ats: {
    application: (id) => `/applications?openId=${encodeURIComponent(id)}`,
    candidate: (id) => `/candidates?candidateId=${encodeURIComponent(id)}`,
    requisition: (id) => `/jobs?openId=${encodeURIComponent(id)}`,
    job: (id) => `/jobs?openId=${encodeURIComponent(id)}`,
    // A colour proposal (ADR 0106 D9): the id IS the palette, which the colour
    // editor fills in unsaved (backoffice-ui utils/lumiPalettePreview.ts).
    branding_preview: (id) => `/portal-settings?lumiColors=${encodeURIComponent(id)}`,
  },
  // Timecard's ids are days and weeks as often as they are rows, so its links
  // carry the date: `[[time/day:2026-03-04|4.3.]]` opens the calendar there.
  time: {
    day: (id) => `/calendar?date=${encodeURIComponent(id)}`,
    week: (id) => `/timesheets?week=${encodeURIComponent(id)}`,
    leave: () => '/leave',
    approval: () => '/approvals',
    project: () => '/projects',
  },
  people: {
    person: (id) => `/persons/${encodeURIComponent(id)}`,
  },
  // The Berichtsheft: a trainee's own week opens on Woche, a week waiting for
  // the trainer on Prüfen.
  apprentice: {
    report: (id) => `/woche/${encodeURIComponent(id)}`,
    review: (id) => `/pruefen/${encodeURIComponent(id)}`,
  },
};
// Legacy module-less tokens (`[[application:id|…]]`) are ATS's — infer the module.
const LEGACY_TYPE_MODULE: Record<string, string> = {
  application: 'ats',
  candidate: 'ats',
  requisition: 'ats',
  job: 'ats',
};

// A page link's path: one leading slash (never `//host`), nothing that leaves the app.
const IN_APP_PATH = /^\/(?!\/)[^\s\\]*$/;

export function createAssistantTransport(opts: AssistantTransportOptions): AssistantTransport {
  return {
    async request(path, init) {
      const locale = opts.identity().locale;
      const config: Record<string, unknown> = {
        url: `/${path}`,
        method: init?.method || 'GET',
        data: init?.body,
        params: init?.query,
        // The UI language, which decides both what the model answers in and what
        // language a module writes its draft summaries in (assistant-api reads
        // X-Locale, and its CORS already allowed it — only the sender was
        // missing, so every conversation came back German).
        ...(locale ? { headers: { 'X-Locale': locale } } : {}),
      };
      // The panel surfaces its own errors (disabled/limit/send). Forwarded only so a
      // host that still passes the option keeps compiling — nothing reads the flag in a
      // converted app (issue 195, A10).
      if (opts.suppressGlobalError) config.suppressGlobalError = true;
      if (path === 'chat') config.timeout = CHAT_TIMEOUT_MS;
      const res = await opts.api.request(config as any);
      return res.data;
    },
    navigate(path, moduleKey) {
      if (moduleKey && moduleKey !== opts.module) {
        const target = opts.moduleTargets().find((t) => t.key === moduleKey);
        if (target?.href) {
          // Cross-module is a full page load (separate SPA); hand off to the peer,
          // preserving the deep-link path (not just the module root).
          window.location.href = withDeepLink(target.href, path);
          return;
        }
      }
      void opts.router.push(path);
    },
    resolveLink(ref) {
      // A place, not a record (`[[ats/page:/portal-settings|…]]`, from a handbook
      // result or a navigate target): the id is the in-module path. Only an
      // absolute in-app path, into this app or a module the switcher offers —
      // navigate() has no other way to get there.
      if (ref.type === 'page') {
        if (!ref.module || !IN_APP_PATH.test(ref.id)) return null;
        if (ref.module !== opts.module && !opts.moduleTargets().some((t) => t.key === ref.module && t.href)) return null;
        return { path: ref.id, moduleKey: ref.module };
      }
      const moduleKey = ref.module || LEGACY_TYPE_MODULE[ref.type];
      const route = moduleKey ? LINK_ROUTES[moduleKey]?.[ref.type] : undefined;
      if (!moduleKey || !route) return null;
      return { path: route(ref.id), moduleKey };
    },
    // The panel's only confirm is "new conversation", which deletes the current
    // one: it goes through the app's shared dialog (NoticeDialogHost), danger tone.
    async confirm({ title, body }) {
      return useDialog().confirm({
        title,
        body,
        variant: 'warning',
        confirmTone: 'danger',
        confirmLabel: opts.confirmLabel?.(),
      });
    },
    // A run the panel saw succeed is a write the page behind the chat has not
    // read yet (#1102): tell this tab's listeners what it touched.
    runSucceeded(run) {
      dispatchLumiChanged({
        module: run.moduleKey ?? null,
        tool: run.toolName ?? null,
        entityType: run.entityType ?? null,
        entityId: run.entityId ?? null,
      });
    },
    context() {
      const id = opts.identity();
      return {
        moduleKey: opts.module,
        route: opts.route(),
        tenantSlug: id.tenantSlug,
        email: id.email,
        locale: id.locale,
      };
    },
  };
}

/** The three per-(tenant,user) keys the panel persists (ADR 0027 §3.8): `mode`
 *  and `conversation` in localStorage, `state` (expanded/minimized) in
 *  sessionStorage — it lasts a browser session, so a deliberate sign-out has to
 *  end it too (see `clearAssistantPanelState`).
 *
 *  Keyed on the USER ID, never the address (issue 698, ADR 0093): a phone-only
 *  account has no address at all, and an address can change while the id cannot.
 *  `subject` is the signed-in user's id, or — while a demo persona is active — the
 *  host's stable persona key, so each persona keeps a conversation of its own. */
export function assistantStorageKeys(tenantSlug: string | undefined, subject: string | undefined): {
  mode: string;
  state: string;
  conversation: string;
} {
  const base = `${tenantSlug || 'default'}:${subject || 'anon'}`;
  return {
    mode: `lumi:shell-mode:${base}`,
    state: `lumi:shell-state:${base}`,
    conversation: `lumi:conversation:${base}`,
  };
}

/** Carry the values an earlier build stored under the ADDRESS-keyed names over to
 *  the id-keyed ones, once (issue 698): without it everybody's open Lumi
 *  conversation and dock mode would be forgotten at the deploy that switched the
 *  keys. A value already under the new key wins; the old key is removed either
 *  way, so this is a no-op from the second call on. Never throws. */
export function migrateLegacyAssistantKeys(
  tenantSlug: string | undefined,
  subject: string | undefined,
  legacyEmail: string | undefined | null,
): void {
  if (!subject || !legacyEmail || subject === legacyEmail) return;
  const next = assistantStorageKeys(tenantSlug, subject);
  const old = assistantStorageKeys(tenantSlug, legacyEmail);
  const move = (store: Storage | undefined, from: string, to: string) => {
    try {
      if (!store) return;
      const value = store.getItem(from);
      if (value === null) return;
      if (store.getItem(to) === null) store.setItem(to, value);
      store.removeItem(from);
    } catch {
      /* storage unavailable — nothing to carry over */
    }
  };
  const local = typeof localStorage === 'undefined' ? undefined : localStorage;
  const session = typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  move(local, old.mode, next.mode);
  move(local, old.conversation, next.conversation);
  move(session, old.state, next.state);
}

const SHELL_STATE_PREFIX = 'lumi:shell-state:';

/** Forget whether the panel was open, for every (tenant,user) in this tab. A
 *  deliberate sign-out calls it: the state is per tab-session, and the next
 *  sign-in in the same tab must start minimized, not reopen onto the previous
 *  session's panel (issue 718). The call site knows no user, so it sweeps the
 *  prefix instead of computing one key. */
export function clearAssistantPanelState(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(SHELL_STATE_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable — the panel just keeps its state */
  }
}
